import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Button, Input, Card } from "@/components/ui";
import { authApi } from "@/services/api";
import { useAuthStore } from "@/stores/authStore";
import { useEmailValidation } from "@/hooks";

interface ProfileFormData {
  name: string;
  email: string;
  phone: string;
  current_password?: string;
  new_password?: string;
  confirm_password?: string;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, updateUser } = useAuthStore();

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ProfileFormData>({
    mode: "onChange",
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const newPassword = watch("new_password");
  const email = watch("email");

  // Email validation hook
  const emailValidation = useEmailValidation(email);

  const updateMutation = useMutation({
    mutationFn: (data: ProfileFormData) => authApi.updateProfile(data),
    onSuccess: (response: any) => {
      toast.success("Perfil atualizado com sucesso");
      if (response.data) {
        updateUser(response.data);
      }
      queryClient.invalidateQueries({ queryKey: ["user"] });
      reset({
        name: response.data?.name || "",
        email: response.data?.email || "",
        phone: response.data?.phone || "",
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    },
    onError: (error: any) => {
      toast.error(error?.message || "Erro ao atualizar perfil");
    },
  });

  const isLoading = updateMutation.isPending;

  const onSubmit = (data: ProfileFormData) => {
    // Validações de senha
    if (data.new_password && !data.current_password) {
      toast.error("Informe sua senha atual para alterar a senha");
      return;
    }

    if (data.new_password && data.new_password.length < 6) {
      toast.error("A nova senha deve ter no mínimo 6 caracteres");
      return;
    }

    if (data.new_password && data.new_password !== data.confirm_password) {
      toast.error("As senhas não conferem");
      return;
    }

    // Limpar campos vazios de senha
    const submitData: any = {
      name: data.name,
      email: data.email,
      phone: data.phone,
    };

    if (data.current_password && data.new_password) {
      submitData.current_password = data.current_password;
      submitData.password = data.new_password;
    }

    updateMutation.mutate(submitData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-primary via-bg-primary to-bg-secondary p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/admin")}
            className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Meu Perfil</h1>
            <p className="text-text-secondary mt-1">
              Atualize suas informações pessoais
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Avatar */}
          <Card className="p-8">
            <div className="flex flex-col items-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center text-white font-semibold text-3xl mb-4">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <p className="text-text-primary font-semibold">{user?.name}</p>
              <p className="text-text-muted text-sm">{user?.email}</p>
              <p className="text-xs text-text-muted mt-4">
                Upload de foto em breve 🚀
              </p>
            </div>
          </Card>

          {/* Informações Pessoais */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Informações Pessoais
            </h2>
            <div className="space-y-4">
              <Input
                label="Nome Completo *"
                format="name"
                {...register("name", { required: "Nome é obrigatório" })}
                error={errors.name?.message}
              />
              <Input
                label="Email *"
                type="email"
                format="email"
                {...register("email", {
                  required: "Email é obrigatório",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Email inválido",
                  },
                })}
                error={errors.email?.message}
                hint={
                  emailValidation.isValidating
                    ? "Validando..."
                    : emailValidation.message
                }
                className={
                  emailValidation.valid === true
                    ? "border-green-500"
                    : emailValidation.valid === false
                      ? "border-red-500"
                      : ""
                }
              />
              <Input
                label="Telefone"
                format="phone"
                {...register("phone")}
                placeholder="(00) 00000-0000"
              />
            </div>
          </Card>

          {/* Alterar Senha */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">
              Alterar Senha
            </h2>
            <div className="space-y-4">
              <Input
                label="Senha Atual"
                type={showCurrentPassword ? "text" : "password"}
                {...register("current_password")}
                error={errors.current_password?.message}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="cursor-pointer"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                }
                hint="Deixe em branco se não deseja alterar a senha"
              />
              <Input
                label="Nova Senha"
                type={showNewPassword ? "text" : "password"}
                {...register("new_password", {
                  minLength: {
                    value: 6,
                    message: "A senha deve ter no mínimo 6 caracteres",
                  },
                })}
                error={errors.new_password?.message}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="cursor-pointer"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                }
              />
              <Input
                label="Confirmar Nova Senha"
                type={showConfirmPassword ? "text" : "password"}
                {...register("confirm_password", {
                  validate: (value) =>
                    !newPassword ||
                    value === newPassword ||
                    "As senhas não conferem",
                })}
                error={errors.confirm_password?.message}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="cursor-pointer"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                }
              />
            </div>
          </Card>

          {/* Ações */}
          <div className="flex items-center justify-end gap-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/admin")}
            >
              Cancelar
            </Button>
            <Button type="submit" isLoading={isLoading}>
              Salvar Alterações
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
